import {
  COMBINED_INTRO_FULL_MODE_MAX_LISTINGS,
  COMBINED_INTRO_MAX_LISTINGS,
} from "@/lib/buyerCombinedIntroRules";

/**
 * Every sentence the combined intro email's screens show, in one place.
 *
 * <p>The combined intro is ONE email to ONE buyer introducing two or more of
 * their listings, started from the buyer's Submissions tab. It is not the
 * single intro (one listing, from that submission's own Buyer intro tab) and not
 * the broker intro (to a listing broker), and nothing here is shared with
 * either — a sentence written for one audience must never be one import away
 * from the other.</p>
 *
 * <p>The numbers in these sentences are read from the shared rules module, never
 * typed, so the explainer cannot say "five" while the server switches at six.</p>
 */
export const combinedIntroCopy = {
  // ---- Selection bar (the Submissions tab) --------------------------------
  selectedCount: (n: number) => `${n} selected`,
  /** Appended to the count when the reader picked rows on more than one page
   *  or ordering — the count includes rows they cannot see. */
  onMoreThanOnePage: "(on more than one page)",
  clear: "Clear",
  /** The count is the listings the email can INCLUDE — ticks it leaves out
   *  are named in the composer, never counted toward the email (D3a). */
  sendButton: (n: number) => `Send intro email (${n})`,
  needTwo: "Select two or more that can go in an intro email. For one listing, open it and use its Buyer intro tab.",
  tooMany: `Up to ${COMBINED_INTRO_MAX_LISTINGS} listings per email.`,

  // ---- Row cells ----------------------------------------------------------
  selectColumnHeader: "Select",
  selectAriaLabel: (listing: string) => `Select ${listing}`,
  /** D3a / R5 — a tick the combined intro leaves out, with the same reason
   *  text as before ("Dead", "Declined by buyer", … or "archived"). */
  leftOutHeading: "Left out of this email",
  leftOut: (reason: string) => `Left out — ${reason}`,
  introSent: (date: string) => `Intro sent ${date}`,

  // ---- Composer -----------------------------------------------------------
  composerTitle: "Intro email to the buyer",
  explainer: (buyerFullName: string, n: number) =>
    `One email to ${buyerFullName} introducing ${n} listings. Read it through and change anything that is not right — it goes to the buyer under your name. Up to ${numberWord(COMBINED_INTRO_FULL_MODE_MAX_LISTINGS)} listings get a paragraph each; more than ${numberWord(COMBINED_INTRO_FULL_MODE_MAX_LISTINGS)} get one line each. It reports only what each listing states.`,
  resumeDraft: (m: number) => `Resume draft (${m} listings)`,
  to: "To",
  noEmailOnFile: "no email address on file",
  listingsHeading: "Listings in this email",
  removeListing: (listing: string) => `Remove ${listing}`,
  draftButton: "Draft the email",
  drafting: "Drafting…",
  draftingNote: "Drafting can take a couple of minutes when each listing gets its own paragraph.",
  /** R4 — asked before a new draft replaces the buyer's unsent one. */
  replaceTitle: "Replace your unsent draft?",
  replaceMessage: (m: number) => `This replaces your unsent draft for ${m} listings.`,
  replaceConfirm: "Replace it",
  replaceCancel: "Keep it",
  subject: "Subject",
  message: "Message",
  messageAriaLabel: "Combined intro email message",
  includeLinks: "Include listing page links",
  noListingPage: "No https listing page on file.",
  listingPagesCaption: "Added below your message exactly as shown. Not editable.",
  /** R4 — a listing removed on screen: the message no longer describes the set. */
  redraftToMatch: "Redraft to match the listings above.",
  /** The message box is empty — why Save, Send, Copy and the listing-page
   *  checkbox are disabled. */
  messageEmpty: "Write a message before you save, send or copy it.",
  /**
   * The server's own refusal of a Phoenix send to a buyer with no address,
   * word for word (`BuyerCombinedIntroEmailService.SendAsync`). Pinned to the C#
   * source by `combinedIntroCopy.parity.test.ts`. Copy is still offered, which
   * is what the sentence's second half promises.
   */
  noEmail:
    "This buyer has no email address on file. Add one to their record, or take the copy and send it from your own email.",
  saveDraft: "Save draft",
  saving: "Saving…",
  draftSaved: "Draft saved.",
  sendFromPhoenix: "Send from Phoenix",
  copyForMyOwnEmail: "Copy for my own email",
  redraft: "Redraft",
  redrafting: "Redrafting…",
  eitherPathRecords: "Either path records this as sent on every listing above, so the buyer never looks uncontacted.",
  sendTitle: "Send the intro email to the buyer",
  sendAudienceNote: "under your name. It does not go to the listing brokers.",
  sendFootnote: (n: number) =>
    `Your signature is added automatically. Sending records the intro email as sent on each of these ${n} listings; it does not change their statuses and it does not send the package.`,
  sentTo: (email: string) => `Sent to ${email}.`,
  discardTitle: "Discard your changes?",
  discardMessage: "Your changes to the message have not been saved.",
  discardConfirm: "Discard",
  discardCancel: "Keep editing",

  // ---- After a send, on each listing it introduced -------------------------
  // These describe the COMBINED email and are read on the single listing's own
  // surfaces (its Buyer intro tab and its send history), because that is where
  // a specialist looks to see whether a listing's buyer has been told about it.
  // `others` is the count of OTHER listings in the email — never this one. The
  // intro card's own summary line ("Sent as part of one email to … covering
  // {N} listings", D9) is `introCardCopy.sentAsPartOf`.
  /** D4 — beside the version in the listing's send history. */
  sentWithOthers: (others: number) =>
    `Sent with ${others} other ${others === 1 ? "listing" : "listings"}`,
} as const;

/** Small counts in words, as the approved explainer states "five". */
function numberWord(n: number): string {
  const words = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
  return words[n] ?? String(n);
}
